/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
// Ember Imports
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { filterBy } from '@ember/object/computed';
// Internal Imports
import { hash } from '@linc-technologies/linced-common/utils/promise';
import moment from 'moment/moment';
import AuTaxType from '@linc-technologies/linced-common/enums/au-tax-type';
import { rejectBy } from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class AddEditLedgerItem extends Component {
  @service
  features;

  @service
  store;

  @service
  strings;

  @tracked
  dialogOpen = false;

  @tracked
  saveInProgress = false;

  @tracked
  errorMessage = '';

  @tracked
  ledgers = [];

  @tracked
  costCentres = [];

  @tracked
  internalOrders = [];

  @tracked
  selectedCostCentre = null;

  @action
  setSelectedCostCentre(value) {
    this.selectedCostCentre = value;
  }

  @tracked
  selectedInternalOrder = null;

  @action
  setSelectedInternalOrder(value) {
    this.selectedInternalOrder = value;
  }

  @tracked
  selectedLedger = null;

  costCentreId = null;
  internalOrderId = null;
  ledgerId = null;

  @tracked
  ledgerItemId = null;

  @tracked
  ledgerItemName = '';

  @action
  setLedgerItemName(value) {
    this.ledgerItemName = value;
  }

  @tracked
  ledgerItemDefaultDescription = '';

  @action
  setLedgerItemDefaultDescription(value) {
    this.ledgerItemDefaultDescription = value;
  }

  @tracked
  ledgerItemDefaultDate = '';

  @action
  setLedgerItemDefaultDate(value) {
    this.ledgerItemDefaultDate = value;
  }

  @tracked
  ledgerItemDefaultAmount = null;

  @action
  setLedgerItemDefaultAmount(value) {
    this.ledgerItemDefaultAmount = value;
  }

  @tracked
  ledgerItemIsTaxable = true;

  @action
  setLedgerItemIsTaxable(value) {
    this.ledgerItemIsTaxable = value;
  }

  @tracked
  ledgerItemIsDonation = false;

  @action
  setLedgerItemIsDonation(value) {
    this.ledgerItemIsDonation = value;
  }

  @tracked
  ledgerItemIsRefundable = true;

  @action
  setLedgerItemIsRefundable(value) {
    this.ledgerItemIsRefundable = value;
  }

  @tracked
  ledgerItemMinDefaultDate = '';

  @tracked
  ledgerItemMaxDefaultDate = '';

  costMappingOption = null;
  event = null;
  option = null;
  selection = null;

  @tracked
  ledgerItemNameViolations = [];

  @action
  setLedgerItemNameViolations(value) {
    this.ledgerItemNameViolations = value;
  }

  @tracked
  ledgerItemDefaultDescriptionViolations = [];

  @action
  setLedgerItemDefaultDescriptionViolations(value) {
    this.ledgerItemDefaultDescriptionViolations = value;
  }

  @computed(
    'ledgerItemNameViolations.[]',
    'ledgerItemDefaultDescriptionViolations.[]',
  )
  get hasLanguageViolations() {
    return (
      !isEmpty(this.ledgerItemNameViolations) ||
      !isEmpty(this.ledgerItemDefaultDescriptionViolations)
    );
  }

  @tracked currentYear = moment().year();
  year = moment().year();

  get nextYear() {
    return this.currentYear + 1;
  }

  @computed('ledgers.[]', 'features.sapFinance', 'ledgerItemId')
  get availableLedgers() {
    if (!this.features.sapFinance || !isEmpty(this.ledgerItemId)) {
      // For most schools, simply filter out hidden Ledgers.
      // The same goes if editing an existing Ledger.
      return rejectBy(this.ledgers, 'isHidden');
    }

    // For SAP schools, filter out hidden and inactive Ledgers.
    return this.ledgers.filter((ledger) => ledger.isActive && !ledger.isHidden);
  }

  @filterBy('costCentres', 'isActive')
  availableCostCentres;

  @filterBy('internalOrders', 'isActive')
  availableInternalOrders;

  @action
  toggleDialog() {
    this.toggleProperty('dialogOpen');
    if (this.dialogOpen) {
      let promises = {
        ledgers: this.store.findAll('finance/ledger'),
      };
      if (this.features.sapFinance) {
        // Add extra SAP entities if required.
        promises.costCentres = this.store.findAll('finance/cost-centre');
        promises.internalOrders = this.store.findAll('finance/internal-order');
      }
      if (this.ledgerItem) {
        // A belongsTo takes a record, not the async proxy.
        promises.costCentre = this.ledgerItem.costCentre;
        promises.internalOrder = this.ledgerItem.internalOrder;
        promises.ledger = this.ledgerItem.ledger;
      }
      hash(promises).then((response) => {
        this.ledgers = response.ledgers;
        this.costCentres = response.costCentres;
        this.internalOrders = response.internalOrders;
        this.ledgerItemMinDefaultDate = `${this.year}-01-01`;
        this.ledgerItemMaxDefaultDate = `${this.year}-12-31`;

        if (this.ledgerItem) {
          this.selectedCostCentre = response.costCentre;
          this.selectedInternalOrder = response.internalOrder;
          this.selectedLedger = response.ledger;
          this.ledgerItemName = this.ledgerItem.get('name');
          this.ledgerItemDefaultDescription =
            this.ledgerItem.get('defaultDescription');
          this.ledgerItemDefaultDate = this.ledgerItem.get('defaultDate');
          this.ledgerItemDefaultAmount = Number(
            this.ledgerItem.get('defaultAmount'),
          ).toFixed(2);
          this.ledgerItemIsTaxable = this.ledgerItem.get('isTaxable');
          this.ledgerItemIsDonation = this.ledgerItem.get('isDonation');
          this.ledgerItemIsRefundable = this.ledgerItem.get('isRefundable');
          this.ledgerItemId = this.ledgerItem.get('id');
        } else {
          this.ledgerItemName =
            this.event.title + (this.option ? ' - ' + this.option.label : '');

          this.ledgerItemDefaultDescription = this.selection
            ? this.selection.label
            : '';

          this.ledgerItemDefaultDate = moment().format('YYYY-MM-DD');

          this.ledgerItemDefaultAmount = this.costMappingOption
            ? this.costMappingOption.amount
            : 0;
        }
      });
    } else {
      this.resetForm();
    }
  }

  @action
  selectLedger(ledger) {
    this.selectedLedger = ledger;
    this.selectedInternalOrder = null;
  }

  @action
  saveItem() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;
    let action = 'create';
    // taxType will only be set (below) for NSW/SAP schools.
    let taxType;
    let isTaxable = this.ledgerItemIsTaxable;
    let isDonation = this.ledgerItemIsDonation;
    if (this.features.sapFinance) {
      taxType = this.get('selectedLedger.taxType');
      isTaxable = taxType === AuTaxType.S1;
      isDonation = this.get('selectedLedger.isDeductibleGiftReceipt');
    }

    if (isEmpty(this.ledgerItemId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('finance/ledger-item', {
          costCentreId: this.selectedCostCentre,
          internalOrderId: this.selectedInternalOrder,
          ledgerId: this.selectedLedger,
          name: this.ledgerItemName,
          defaultDescription: this.ledgerItemDefaultDescription,
          defaultDate: this.ledgerItemDefaultDate,
          defaultAmount: this.ledgerItemDefaultAmount,
          isTaxable,
          isDonation,
          isRefundable: this.ledgerItemIsRefundable,
          taxType,
          event: this.event,
        });
        resolve(record);
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        const id = this.ledgerItemId;
        this.store.findRecord('finance/ledger-item', id).then((ledger) => {
          ledger.setProperties({
            costCentreId: this.selectedCostCentre,
            internalOrderId: this.selectedInternalOrder,
            name: this.ledgerItemName,
            defaultDescription: this.ledgerItemDefaultDescription,
            defaultDate: this.ledgerItemDefaultDate,
            defaultAmount: this.ledgerItemDefaultAmount,
            isTaxable,
            isDonation,
            isRefundable: this.ledgerItemIsRefundable,
            taxType,
          });

          resolve(ledger);
        });
      });
    }

    promise.then((record) => {
      return record
        .save()
        .then((record) => {
          if (action === 'create') {
            if (this.addLedgerItem) {
              this.addLedgerItem(record, this.option, this.selection);
            }
            if (this.addIntentionToAttendLedgerItem) {
              this.addIntentionToAttendLedgerItem(
                this.costMappingOption,
                record,
              );
            }
          }

          this.dialogOpen = false;
          this.resetForm();
        })
        .catch((err) => {
          this.handleError(err, action);
          // The form still holds the edits for a retry; rollbackAttributes()
          // alone would leave the cost centre and internal order.
          rollbackRecord(record);
        })
        .then(() => {
          this.saveInProgress = false;
        });
    });
  }

  resetForm() {
    this.errorMessage = '';
    this.selectedCostCentre = null;
    this.selectedInternalOrder = null;
    this.selectedLedger = null;
    this.ledgerItemId = null;
    this.ledgerItemName = '';
    this.ledgerItemDefaultDescription = '';
    this.ledgerItemDefaultDate = '';
    this.ledgerItemDefaultAmount = null;
    this.ledgerItemIsTaxable = true;
    this.ledgerItemIsDonation = false;
    this.ledgerItemIsRefundable = true;
    this.ledgerItemMinDefaultDate = `${this.currentYear}-01-01`;
    this.ledgerItemMaxDefaultDate = `${this.nextYear}-12-31`;
    this.ledgerItemNameViolations = [];
    this.ledgerItemDefaultDescriptionViolations = [];
  }

  handleError(err) {
    if (!isEmpty(err.errors)) {
      if (!isEmpty(err.errors.msg)) {
        this.errorMessage = err.errors.msg;
      } else if (!isEmpty(err.errors[0])) {
        let error = err.errors[0];
        if (!isEmpty(error.detail)) {
          this.errorMessage = error.detail;
        }
      }
    } else {
      this.errorMessage = err.message;
    }
  }
}
